import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';

/**
 * Preferências por usuário persistidas no DB pra seguir o usuário entre devices.
 *
 *   - `loading`: true até o GET inicial responder. NÃO usar nenhuma pref antes
 *     disso (evita salvar default por cima de valor real do DB num device novo).
 *   - `preferences`: objeto plano com as chaves whitelisted no backend
 *     (ex: { operacoes_layout_mode, operacoes_last_preset_id }).
 *   - `setPreference(key, value)`: atualiza local imediatamente (otimista) +
 *     dispara PATCH debounced 500ms. Múltiplas chamadas próximas viram um
 *     único request com o merge de tudo.
 *
 * O que NÃO entra aqui (fica em localStorage): resize de colunas, split ratio,
 * scroll, sortCol/Dir — coisas momentâneas que não fazem sentido sincronizar.
 */
export function useUserPreferences() {
    const [preferences, setPreferences] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Buffer de patches pendentes pra coalescer múltiplas mudanças num único
    // PATCH. Ref pra não disparar re-render — só o setTimeout consome.
    const pendingPatchRef = useRef({});
    const flushTimerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await api.getUserPreferences();
                if (cancelled) return;
                const settings = resp?.settings || {};
                setPreferences(settings);
            } catch (err) {
                if (cancelled) return;
                setError({
                    message: err?.message || 'Não foi possível carregar preferências.',
                    code: err?.code || 'OPS27_PREFS_LOAD',
                });
                console.error('[operacoes/preferences/load]', err?.code || 'OPS27_PREFS_LOAD', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const flushNow = useCallback(async () => {
        const patch = pendingPatchRef.current;
        pendingPatchRef.current = {};
        if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
        }
        if (Object.keys(patch).length === 0) return;
        try {
            await api.patchUserPreferences(patch);
        } catch (err) {
            // Falha de save NÃO reverte o estado local — UX prioriza responsividade.
            // Próxima mudança tenta novamente; reload via outro device pega o valor
            // real. Log pra investigar se virar padrão.
            console.error('[operacoes/preferences/save]', err?.code || 'OPS27_PREFS_SAVE', err);
        }
    }, []);

    const setPreference = useCallback(
        (key, value) => {
            setPreferences((prev) => {
                if (prev[key] === value) return prev;
                return { ...prev, [key]: value };
            });
            pendingPatchRef.current[key] = value;
            if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
            flushTimerRef.current = setTimeout(flushNow, 500);
        },
        [flushNow],
    );

    // Flush pendente se o componente desmontar — evita perder o último patch
    // quando o user fecha a aba logo após mudar algo.
    useEffect(() => {
        return () => {
            if (Object.keys(pendingPatchRef.current).length > 0) {
                flushNow();
            }
        };
    }, [flushNow]);

    return { preferences, loading, error, setPreference };
}
